// API bindings: services (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { ServiceResponse, ServiceCreateRequest, ServiceListResponse, ServiceUpdateRequest } from "./definitions";

export class ApiServices {
    /**
     * Method: POST
     * Path: /projects/{projectId}/services
     * Creates a service inside a project.
     * @param projectId Project identifier
     * @param body Body parameters
     * @returns The request parameters
     */
    public static CreateService(projectId: string, body: ServiceCreateRequest): RequestParams<ServiceResponse, CreateServiceErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/projects/${encodeURIComponent(projectId)}/services`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "NO_FIELDS", handler.badRequestNoFields)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "INVALID_BODY", handler.badRequestInvalidBody)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /projects/{projectId}/services
     * Lists the services belonging to a project.
     * @param projectId Project identifier
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListProjectServices(projectId: string, queryParams: ListProjectServicesQueryParameters): RequestParams<ServiceListResponse, ListProjectServicesErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/projects/${encodeURIComponent(projectId)}/services` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "NO_FIELDS", handler.badRequestNoFields)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "INVALID_BODY", handler.badRequestInvalidBody)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /services/{id}
     * Gets a service.
     * @param id Service identifier
     * @returns The request parameters
     */
    public static GetService(id: string): RequestParams<ServiceResponse, GetServiceErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/services/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: PUT
     * Path: /services/{id}
     * Updates a service.
     * @param id Service identifier
     * @param body Body parameters
     * @returns The request parameters
     */
    public static UpdateService(id: string, body: ServiceUpdateRequest): RequestParams<ServiceResponse, UpdateServiceErrorHandler> {
        return {
            method: "PUT",
            url: getApiUrl(`/services/${encodeURIComponent(id)}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "NO_FIELDS", handler.badRequestNoFields)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "INVALID_BODY", handler.badRequestInvalidBody)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /services/{id}
     * Deletes a service.
     * @param id Service identifier
     * @returns The request parameters
     */
    public static DeleteService(id: string): RequestParams<void, DeleteServiceErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/services/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for CreateService
 */
export type CreateServiceErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The request contains fields that cannot be changed
     */
    badRequestInvalidFields: () => void;

    /**
     * The request contains values with invalid types
     */
    badRequestInvalidBody: () => void;

    /**
     * The service name is missing, empty or too long
     */
    badRequestInvalidName: () => void;

    /**
     * The service description is too long
     */
    badRequestInvalidDescription: () => void;

    /**
     * The update request contains no editable fields
     */
    badRequestNoFields: () => void;

    /**
     * The pagination parameters are invalid
     */
    badRequestInvalidPagination: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ListProjectServices
 */
export type ListProjectServicesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The request contains fields that cannot be changed
     */
    badRequestInvalidFields: () => void;

    /**
     * The request contains values with invalid types
     */
    badRequestInvalidBody: () => void;

    /**
     * The service name is missing, empty or too long
     */
    badRequestInvalidName: () => void;

    /**
     * The service description is too long
     */
    badRequestInvalidDescription: () => void;

    /**
     * The update request contains no editable fields
     */
    badRequestNoFields: () => void;

    /**
     * The pagination parameters are invalid
     */
    badRequestInvalidPagination: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Query parameters for ListProjectServices
 */
export interface ListProjectServicesQueryParameters {
    /**
     * Page number, starting at 1.
     */
    page?: number;

    /**
     * Number of services per page, maximum 100.
     */
    pageSize?: number;
}

/**
 * Error handler for GetService
 */
export type GetServiceErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for UpdateService
 */
export type UpdateServiceErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The request contains fields that cannot be changed
     */
    badRequestInvalidFields: () => void;

    /**
     * The request contains values with invalid types
     */
    badRequestInvalidBody: () => void;

    /**
     * The service name is missing, empty or too long
     */
    badRequestInvalidName: () => void;

    /**
     * The service description is too long
     */
    badRequestInvalidDescription: () => void;

    /**
     * The update request contains no editable fields
     */
    badRequestNoFields: () => void;

    /**
     * The pagination parameters are invalid
     */
    badRequestInvalidPagination: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for DeleteService
 */
export type DeleteServiceErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;
};
